import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/services/api.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { jwtDecode } from 'jwt-decode';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from '../../common/error-dialog/error-dialog.component';
import { SuccessDialogComponent } from '../../common/success-dialog/success-dialog.component';


// Define an interface that reflects the structure of your job details data
interface JobDetails {
  id: string;
  title: string;
  description: SafeHtml;
  skills: SafeHtml; 
  education: SafeHtml; 
  salary: any;
  experience: any;
  qualification: any;
  logo: any;
  companyName: any;
  organizationId: any;
}

interface AvailableInterval {
  start: string;
  end: string;
  date: string;
  time_slot_id: number;
}


interface TimeSlot {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  booths: number[];
  availableIntervals: AvailableInterval[];
}



@Component({
  selector: 'app-job-details',
  templateUrl: './job-details.component.html',
  styleUrls: ['./job-details.component.scss']
})
export class JobDetailsComponent implements OnInit {
  jobId: any | null;
  jobDetails: JobDetails | null = null;
  hasApplied = false;
  isLoggedIn = false;
  isApplying: boolean = false; 
  isTokenValidating = false;
  availableTimeSlots: any[] = [];
  noAvailableSlotsMessage: string = '';
  selectedTimeSlot: TimeSlot | null = null;
  selectedInterval: AvailableInterval | null = null; 
  bookingDetails: any[] = [];
  isLoading = false;



  constructor(
    private route: ActivatedRoute,
    private apiService: ApiService,
    private sanitizer: DomSanitizer,
    private router: Router, 
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    
    this.jobId = this.route.snapshot.paramMap.get('id');
    if (this.jobId) {
      this.fetchJobDetails(this.jobId);
      this.checkApplication(this.jobId);
      this.getAvailableSlots();
    }
    this.checkLoginStatus();

  
  }


  checkLoginStatus(): void {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded: any = jwtDecode(token);
        this.isLoggedIn = decoded.exp > Date.now() / 1000;
      } catch (error) {
        console.error("Error decoding token:", error);
        this.isLoggedIn = false;
      }
    } else {
      this.isLoggedIn = false;
    }
  }

  checkApplication(jobId: any) {
    this.apiService.checkUserApplication(this.jobId).subscribe({

      next: (response) => this.hasApplied = response.hasApplied,
      
      
      error: (error) => console.error('Error checking application:', error)
    });

  }

  fetchJobDetails(jobId: string): void {
    this.apiService.getJobDetailsById(jobId).subscribe({
      next: (data) => {
        console.log(data);
        // Sanitize the HTML content of description, skills, and education fields
        const sanitizedDescription = this.sanitizer.bypassSecurityTrustHtml(data.description);
        const sanitizedSkills = this.sanitizer.bypassSecurityTrustHtml(data.skills);
        const sanitizedEducation = this.sanitizer.bypassSecurityTrustHtml(data.education);
        
        // Assign sanitized fields back to the data object
        this.jobDetails = { 
          ...data, 
          description: sanitizedDescription,
          skills: sanitizedSkills,
          education: sanitizedEducation
        };
      },
      error: (error) => console.error('Error fetching job details:', error)
    });
  }


  validateAndApplyForJob(jobId: any, orgId: any): void {
    this.isTokenValidating = true;
    this.apiService.validateToken().subscribe({
      next: (response) => {
        if (response.valid) {
          // Token is valid, now check for CV
          this.apiService.checkUserCv().subscribe({
            next: (cvResponse) => {
              if (cvResponse.hasCv) {
                // User has uploaded a CV, proceed to apply for the job
                this.applyForJob(jobId, orgId);
              } else {
                // No CV uploaded, show error dialog
                this.dialog.open(ErrorDialogComponent, {
                  width: '300px',
                  data: { message: 'Porfabor "upload" bo CV promé ku bo por apliká pa un trabou.' }
                });
              }
            }
          });
        } else {
          localStorage.removeItem('token');
          this.isLoggedIn = false;
          this.router.navigate(['/login']);
        }
      },
      error: (error) => {
        console.error('Token validation error:', error);
        localStorage.removeItem('token');
        this.isLoggedIn = false;
        this.router.navigate(['/login']);
      },
      complete: () => this.isTokenValidating = false
    });
  }
  
  applyForJob(jobId: any, orgId: any): void {
    if (!this.isLoggedIn || this.isApplying) {
      return;
    }
    this.isApplying = true; // Start applying process
    this.apiService.applyForJob(jobId, orgId).subscribe({
      next: (response) => {
        console.log('Application successful', response);
        this.hasApplied = true;
      },
      error: (error) => {
        console.error('Error applying for the job', error);
      },
      complete: () => {
        this.isApplying = false; // Reset applying flag
      }
    });
  }
  
  

  getAvailableSlots(): void {
    this.apiService.getAvailableTimeSlots(this.jobId!).subscribe({
      next: (response) => {
        if (response.message && response.bookingDetails) {
          this.noAvailableSlotsMessage = response.message;
          this.availableTimeSlots = [];
          this.bookingDetails = response.bookingDetails;
          console.log(response);
          console.log(this.bookingDetails);
        } else if (response.message) {
          this.noAvailableSlotsMessage = response.message;
          this.availableTimeSlots = [];
          this.bookingDetails = [];
          console.log(response);
        } else {
          this.availableTimeSlots = response.map(slot => ({
            ...slot,
            availableIntervals: slot.availableIntervals.map(interval => ({
              ...interval,
              time_slot_id: slot.id
            }))
          }));
          this.noAvailableSlotsMessage = '';
          this.bookingDetails = []; // Reset booking details in case of new data
          console.log(this.availableTimeSlots);
        }
      },
      error: (error) => {
        console.error('Error fetching available slots:', error);
      }
    });

  
  }
  


  bookTimeSlot(): void {
    if (!this.selectedInterval || !this.jobId) {
      console.error("Please select a time slot or job ID is missing.");
      return;
    }
  
    // Extract necessary information
    const { start, end, date, time_slot_id: timeSlotId } = this.selectedInterval;
    const jobId = Number(this.jobId);
  
    // Check for valid timeSlotId and date
    if (!timeSlotId || !date) {
      console.error("Time slot ID or date not found.");
      return;
    }
  
    this.isLoading = true; // Start loading
  
    this.apiService.bookTimeSlot(timeSlotId, date, start, end, jobId).subscribe({
      next: (response) => {
        console.log("Booking successful: ", response);
        this.getAvailableSlots();
        this.isLoading = false; // Stop loading
      },
      error: (error) => {
        console.error("Error booking time slot: ", error);
        this.isLoading = false; // Stop loading
      }
    });
  }
  



  
  // Utility method to show error dialog
  private showErrorDialog(message: string): void {
    this.dialog.open(ErrorDialogComponent, {
      width: '300px',
      data: { message }
    });
  }
  
  // Utility method to show success dialog
  private showSuccessDialog(message: string): void {
    this.dialog.open(SuccessDialogComponent, {
      width: '300px',
      data: { message }
    });
  }
  

  
}
